// src/components/LocationModal/index.js
// ============================================
// SWIGGY-STYLE LOCATION MODAL
// ============================================
// Features:
// - Use Current Location (GPS)
// - Add New Address (opens AddressModal form)
// - Saved Addresses with selected badge + distance
// - Search for a Location (autocomplete)
// - Recently searched locations (localStorage)
// - Delivery radius awareness
// ============================================

import React, { useState, useEffect, useMemo } from 'react';
import { X, MapPin, Navigation, Search, AlertCircle, Plus, Clock, ChevronRight, Check, Home, Briefcase, MapPinOff } from 'lucide-react';
import { calculateDistance } from '../../utils/storeUtils';
import './index.css';

// Recently searched locations key
const RECENT_LOCATIONS_KEY = 'recently_searched_locations';
const MAX_RECENT_LOCATIONS = 5;

const LocationModal = ({
    isOpen,
    onClose,
    onLocationUpdate,
    currentLocation,
    // NEW: Swiggy-style props
    addresses = [],
    selectedAddress = null,
    onAddressSelect,
    isLoggedIn = false,
    onOpenAddressForm,
    userLocation = null
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDetecting, setIsDetecting] = useState(false);
    const [error, setError] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [detectionStatus, setDetectionStatus] = useState('');
    const [recentLocations, setRecentLocations] = useState([]);
    const [showAllAddresses, setShowAllAddresses] = useState(false);

    // Load recent locations from localStorage
    useEffect(() => {
        if (isOpen) {
            try {
                const stored = localStorage.getItem(RECENT_LOCATIONS_KEY);
                if (stored) {
                    setRecentLocations(JSON.parse(stored));
                }
            } catch (e) {
                console.error('Error loading recent locations:', e);
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setError('');
            setSearchResults([]);
            setDetectionStatus('');
            setShowAllAddresses(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (searchQuery.trim().length > 2) {
            const debounceTimer = setTimeout(() => {
                searchLocation();
            }, 500);
            return () => clearTimeout(debounceTimer);
        } else {
            setSearchResults([]);
        }
    }, [searchQuery]);

    // Calculate distance for each saved address from user's current location
    const addressesWithDistance = useMemo(() => {
        if (!addresses || addresses.length === 0) return [];

        return addresses.map(addr => {
            let distance = null;
            if (userLocation?.latitude && userLocation?.longitude && addr.latitude && addr.longitude) {
                distance = calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    parseFloat(addr.latitude),
                    parseFloat(addr.longitude)
                );
            }
            return { ...addr, distance };
        }).sort((a, b) => {
            // Default first, then by distance
            if (a.is_default && !b.is_default) return -1;
            if (!a.is_default && b.is_default) return 1;
            if (a.distance !== null && b.distance !== null) return a.distance - b.distance;
            return 0;
        });
    }, [addresses, userLocation]);

    // Save a recent location
    const saveRecentLocation = (locationData) => {
        try {
            const stored = localStorage.getItem(RECENT_LOCATIONS_KEY);
            let recents = stored ? JSON.parse(stored) : [];

            // Remove duplicate if exists
            recents = recents.filter(r =>
                r.display_name !== locationData.display_name
            );

            // Add to front
            recents.unshift({
                display_name: locationData.display_name,
                mainText: locationData.mainText || locationData.display_name.split(',')[0],
                subText: locationData.subText || locationData.display_name,
                lat: locationData.lat || locationData.latitude,
                lon: locationData.lon || locationData.longitude,
                timestamp: Date.now()
            });

            // Keep only MAX_RECENT_LOCATIONS
            recents = recents.slice(0, MAX_RECENT_LOCATIONS);

            localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(recents));
            setRecentLocations(recents);
        } catch (e) {
            console.error('Error saving recent location:', e);
        }
    };

    const detectCurrentLocation = () => {
        console.log('🎯 Starting location detection...');
        setIsDetecting(true);
        setError('');
        setDetectionStatus('Requesting location permission...');

        if (!navigator.geolocation) {
            const errorMsg = 'Geolocation is not supported by your browser. Please use search instead.';
            console.error('❌', errorMsg);
            setError(errorMsg);
            setDetectionStatus('');
            setIsDetecting(false);
            localStorage.setItem('location_requested', 'unsupported');
            return;
        }

        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
                console.log('📍 Current permission status:', permissionStatus.state);
                setDetectionStatus(`Permission status: ${permissionStatus.state}`);
            });
        }

        setDetectionStatus('Detecting your location...');

        navigator.geolocation.getCurrentPosition(
            (position) => {
                console.log('✅ Location detected successfully:', {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy
                });

                const locationData = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: new Date().toISOString()
                };

                reverseGeocode(locationData.latitude, locationData.longitude)
                    .then((address) => {
                        console.log('📍 Reverse geocoded address:', address);

                        localStorage.setItem('user_location', JSON.stringify(locationData));
                        localStorage.setItem('location_display', address);
                        localStorage.setItem('location_requested', 'granted');

                        // Save to recent
                        saveRecentLocation({
                            display_name: address,
                            mainText: 'Current Location',
                            subText: address,
                            latitude: locationData.latitude,
                            longitude: locationData.longitude
                        });

                        const event = new CustomEvent('locationUpdated', { detail: locationData });
                        window.dispatchEvent(event);

                        window.dispatchEvent(new StorageEvent('storage', {
                            key: 'user_location',
                            newValue: JSON.stringify(locationData),
                            oldValue: localStorage.getItem('user_location'),
                            url: window.location.href
                        }));

                        if (onLocationUpdate) {
                            onLocationUpdate(locationData);
                        }

                        setDetectionStatus('Location detected successfully!');
                        setIsDetecting(false);

                        setTimeout(() => { onClose(); }, 800);
                    })
                    .catch((err) => {
                        console.error('⚠️ Geocoding failed:', err);

                        localStorage.setItem('user_location', JSON.stringify(locationData));
                        localStorage.setItem('location_display', 'Your Location');
                        localStorage.setItem('location_requested', 'granted');

                        const event = new CustomEvent('locationUpdated', { detail: locationData });
                        window.dispatchEvent(event);

                        window.dispatchEvent(new StorageEvent('storage', {
                            key: 'user_location',
                            newValue: JSON.stringify(locationData)
                        }));

                        if (onLocationUpdate) {
                            onLocationUpdate(locationData);
                        }

                        setDetectionStatus('Location detected!');
                        setIsDetecting(false);

                        setTimeout(() => { onClose(); }, 800);
                    });
            },
            (err) => {
                console.error('❌ Geolocation error:', { code: err.code, message: err.message });
                setIsDetecting(false);
                setDetectionStatus('');
                localStorage.setItem('location_requested', 'denied');

                let errorMessage = '';
                switch (err.code) {
                    case 1:
                        errorMessage = 'Location access denied. Please enable location permissions in your browser settings and try again.';
                        break;
                    case 2:
                        errorMessage = 'Location information is unavailable. Please check your device settings or try searching for your location.';
                        break;
                    case 3:
                        errorMessage = 'Location request timed out. Please try again or use the search option.';
                        break;
                    default:
                        errorMessage = 'Unable to detect location. Please try searching for your location instead.';
                }
                setError(errorMessage);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            }
        );
    };

    const reverseGeocode = async (lat, lon) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=en`
            );

            if (response.ok) {
                const data = await response.json();
                
                // ✅ NEW: Capture and store pincode as fallback for cart analysis
                if (data?.address?.postcode) {
                    console.log('📬 Detected pincode (GPS):', data.address.postcode);
                    localStorage.setItem('detected_pincode', data.address.postcode);
                }

                if (data && data.display_name) {
                    return data.display_name;
                }
                return 'Your Location';
            } else {
                throw new Error('Geocoding failed');
            }
        } catch (error) {
            console.error('Geocoding error:', error);
            return 'Your Location';
        }
    };

    const searchLocation = async () => {
        if (!searchQuery.trim()) {
            setError('Please enter a location');
            return;
        }

        setIsSearching(true);
        setError('');

        try {
            let lat = 17.3850;
            let lon = 78.4867;

            const savedLocation = localStorage.getItem('user_location');
            if (savedLocation) {
                try {
                    const parsed = JSON.parse(savedLocation);
                    if (parsed.latitude && parsed.longitude) {
                        lat = parsed.latitude;
                        lon = parsed.longitude;
                    }
                } catch (e) {
                    console.error('Error parsing saved location for search bias:', e);
                }
            }

            const response = await fetch(
                `https://photon.komoot.io/api/?q=${encodeURIComponent(searchQuery)}&limit=10&lat=${lat}&lon=${lon}`
            );

            if (response.ok) {
                const data = await response.json();

                if (data.features && data.features.length > 0) {
                    const formattedResults = data.features.map(feature => {
                        const props = feature.properties;
                        const name = props.name;
                        const street = props.street;
                        const district = props.district;
                        const city = props.city || props.town || props.village;
                        const state = props.state;

                        let mainText = name || street || city || 'Unknown Location';
                        let subParts = [];
                        if (name && street && name !== street) subParts.push(street);
                        if (district && district !== city && district !== mainText) subParts.push(district);
                        if (city && city !== mainText) subParts.push(city);
                        if (state && state !== mainText) subParts.push(state);

                        let subText = subParts.join(', ');
                        const display_name = subText ? `${mainText}, ${subText}` : mainText;

                        // Calculate distance from user location
                        let distance = null;
                        if (userLocation?.latitude && userLocation?.longitude) {
                            distance = calculateDistance(
                                userLocation.latitude,
                                userLocation.longitude,
                                feature.geometry.coordinates[1],
                                feature.geometry.coordinates[0]
                            );
                        }

                        return {
                            display_name,
                            mainText,
                            subText,
                            lat: feature.geometry.coordinates[1],
                            lon: feature.geometry.coordinates[0],
                            distance,
                            postcode: props.postcode // ✅ Capture postcode from search result
                        };
                    });
                    setSearchResults(formattedResults);
                } else {
                    setSearchResults([]);
                    if (searchQuery.trim().length > 2) {
                        setError('No locations found. Try a different search term.');
                    }
                }
            } else {
                setError('Failed to search locations. Please try again.');
            }
        } catch (err) {
            console.error('❌ Search error:', err);
            setError('Failed to search locations. Please check your internet connection.');
        } finally {
            setIsSearching(false);
        }
    };

    const selectSearchResult = (result) => {
        console.log('📍 Selected location:', result.display_name);

        const locationData = {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
            timestamp: new Date().toISOString()
        };

        localStorage.setItem('user_location', JSON.stringify(locationData));
        localStorage.setItem('location_display', result.display_name);
        localStorage.setItem('location_requested', 'granted');

        // ✅ NEW: Capture and store pincode from search result
        if (result.postcode) {
            console.log('📬 Detected pincode (Search):', result.postcode);
            localStorage.setItem('detected_pincode', result.postcode);
        }

        // Save to recent
        saveRecentLocation(result);

        const event = new CustomEvent('locationUpdated', { detail: locationData });
        window.dispatchEvent(event);

        window.dispatchEvent(new StorageEvent('storage', {
            key: 'user_location',
            newValue: JSON.stringify(locationData),
            oldValue: localStorage.getItem('user_location'),
            url: window.location.href
        }));

        if (onLocationUpdate) {
            onLocationUpdate(locationData);
        }

        onClose();
    };

    const handleAddressSelect = (address) => {
        console.log('📍 Saved address selected:', address.label);

        // Update location based on address coordinates
        if (address.latitude && address.longitude) {
            const locationData = {
                latitude: parseFloat(address.latitude),
                longitude: parseFloat(address.longitude),
                timestamp: new Date().toISOString()
            };

            localStorage.setItem('user_location', JSON.stringify(locationData));
            const displayText = `${address.label}: ${address.address}`;
            localStorage.setItem('location_display', displayText);
            localStorage.setItem('location_requested', 'granted');

            const event = new CustomEvent('locationUpdated', { detail: locationData });
            window.dispatchEvent(event);

            window.dispatchEvent(new StorageEvent('storage', {
                key: 'user_location',
                newValue: JSON.stringify(locationData)
            }));

            if (onLocationUpdate) {
                onLocationUpdate(locationData);
            }
        }

        if (onAddressSelect) {
            onAddressSelect(address);
        }

        onClose();
    };

    const handleRecentLocationClick = (recent) => {
        selectSearchResult({
            display_name: recent.display_name,
            mainText: recent.mainText,
            subText: recent.subText,
            lat: recent.lat,
            lon: recent.lon
        });
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            searchLocation();
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setError('');
    };

    const formatDistance = (km) => {
        if (km === null || km === undefined) return '';
        if (km < 1) return `${Math.round(km * 1000)}m`;
        return `${km.toFixed(1)} km`;
    };

    const getAddressIcon = (label) => {
        const lowerLabel = (label || '').toLowerCase();
        if (lowerLabel.includes('home')) return <Home size={18} />;
        if (lowerLabel.includes('office') || lowerLabel.includes('work')) return <Briefcase size={18} />;
        return <MapPin size={18} />;
    };

    if (!isOpen) return null;

    const displayedAddresses = showAllAddresses
        ? addressesWithDistance
        : addressesWithDistance.slice(0, 3);

    const hasSearchContent = searchQuery.trim().length > 0;

    return (
        <div className="location-modal-overlay" onClick={onClose}>
            <div className="location-modal-swiggy" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="loc-modal-header">
                    <button onClick={onClose} className="loc-modal-back">
                        <X size={22} />
                    </button>
                    <h2 className="loc-modal-title">Select Your Location</h2>
                </div>

                {/* Search Bar */}
                <div className="loc-search-wrapper">
                    <div className="loc-search-box">
                        <Search size={18} className="loc-search-icon" />
                        <input
                            type="text"
                            className="loc-search-input"
                            placeholder="Search an area or address"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            autoFocus={window.innerWidth > 768}
                        />
                        {isSearching && (
                            <span className="loc-searching-badge">Searching...</span>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="loc-modal-body">

                    {/* Error */}
                    {error && (
                        <div className="loc-error-msg">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Detection Status */}
                    {detectionStatus && (
                        <div className="loc-detecting-msg">
                            <div className="loc-detecting-spinner"></div>
                            <span>{detectionStatus}</span>
                        </div>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <div className="loc-section">
                            <div className="loc-results-list">
                                {searchResults.map((result, index) => (
                                    <button
                                        key={index}
                                        className="loc-result-item"
                                        onClick={() => selectSearchResult(result)}
                                    >
                                        <div className="loc-result-icon-wrap">
                                            <MapPin size={18} />
                                        </div>
                                        <div className="loc-result-text">
                                            <div className="loc-result-name">{result.mainText}</div>
                                            <div className="loc-result-address">{result.subText}</div>
                                        </div>
                                        {result.distance !== null && (
                                            <span className="loc-result-distance">
                                                {formatDistance(result.distance)}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Main Content - show when not searching */}
                    {!hasSearchContent && searchResults.length === 0 && (
                        <>
                            {/* Quick Action Cards */}
                            <div className="loc-quick-actions">
                                <button
                                    className="loc-quick-card gps"
                                    onClick={detectCurrentLocation}
                                    disabled={isDetecting}
                                >
                                    <div className="loc-quick-icon gps-icon">
                                        <Navigation size={20} />
                                    </div>
                                    <div className="loc-quick-text">
                                        <span className="loc-quick-label">
                                            {isDetecting ? 'Detecting...' : 'Use Current Location'}
                                        </span>
                                        <span className="loc-quick-hint">Using GPS</span>
                                    </div>
                                </button>

                                {isLoggedIn && onOpenAddressForm && (
                                    <button
                                        className="loc-quick-card add"
                                        onClick={() => {
                                            onClose();
                                            onOpenAddressForm();
                                        }}
                                    >
                                        <div className="loc-quick-icon add-icon">
                                            <Plus size={20} />
                                        </div>
                                        <div className="loc-quick-text">
                                            <span className="loc-quick-label">Add New Address</span>
                                            <span className="loc-quick-hint">Save for later</span>
                                        </div>
                                    </button>
                                )}
                            </div>

                            {/* Saved Addresses Section */}
                            {isLoggedIn && addressesWithDistance.length > 0 && (
                                <div className="loc-section">
                                    <div className="loc-section-header">
                                        <span className="loc-section-title">SAVED ADDRESSES</span>
                                    </div>
                                    <div className="loc-addresses-list">
                                        {displayedAddresses.map((addr) => {
                                            const isSelected = selectedAddress?.id === addr.id;
                                            return (
                                                <button
                                                    key={addr.id}
                                                    className={`loc-address-item ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleAddressSelect(addr)}
                                                >
                                                    <div className="loc-address-icon-wrap">
                                                        {getAddressIcon(addr.label)}
                                                    </div>
                                                    <div className="loc-address-content">
                                                        <div className="loc-address-top">
                                                            <span className="loc-address-label">{addr.label}</span>
                                                            {isSelected && (
                                                                <span className="loc-selected-badge">
                                                                    <Check size={10} />
                                                                    SELECTED
                                                                </span>
                                                            )}
                                                            {addr.is_default && !isSelected && (
                                                                <span className="loc-default-badge">DEFAULT</span>
                                                            )}
                                                        </div>
                                                        <div className="loc-address-text">
                                                            {addr.address}
                                                            {addr.apartment && `, ${addr.apartment}`}
                                                            {addr.city && `, ${addr.city}`}
                                                            {addr.state && `, ${addr.state}`}
                                                            {(addr.post_code || addr.zip_code) && ` - ${addr.post_code || addr.zip_code}`}
                                                        </div>
                                                    </div>

                                                    <ChevronRight size={16} className="loc-address-chevron" />
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {addressesWithDistance.length > 3 && (
                                        <button
                                            className="loc-view-all-btn"
                                            onClick={() => setShowAllAddresses(!showAllAddresses)}
                                        >
                                            {showAllAddresses
                                                ? 'Show less'
                                                : `View all (${addressesWithDistance.length})`
                                            }
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Recently Searched */}
                            {recentLocations.length > 0 && (
                                <div className="loc-section">
                                    <div className="loc-section-header">
                                        <span className="loc-section-title">RECENTLY SEARCHED</span>
                                    </div>
                                    <div className="loc-recent-list">
                                        {recentLocations.map((recent, index) => (
                                            <button
                                                key={index}
                                                className="loc-recent-item"
                                                onClick={() => handleRecentLocationClick(recent)}
                                            >
                                                <div className="loc-recent-icon-wrap">
                                                    <Clock size={16} />
                                                </div>
                                                <div className="loc-recent-text">
                                                    <div className="loc-recent-name">{recent.mainText}</div>
                                                    <div className="loc-recent-address">{recent.subText}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Prompt for non-logged in users */}
                            {!isLoggedIn && addresses.length === 0 && recentLocations.length === 0 && (
                                <div className="loc-login-prompt">
                                    <MapPin size={32} className="loc-login-prompt-icon" />
                                    <p className="loc-login-prompt-text">
                                        Log in to see your saved addresses and get a personalized experience
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LocationModal;